import { 
  faDna, faAtom, faMicroscope, faChartLine, faBrain, faHeart, 
  faVirus, faEye, faLungs, faKidneys, faFlask, faMagnifyingGlass,
  faDroplet
} from '@fortawesome/free-solid-svg-icons';

export const discoveriesData = [
  {
    id: 1,
    title: "Revolutionary CRISPR-Cas13 RNA Editing for Neurological Disorders",
    category: "Gene Therapy",
    icon: faDna,
    preview: "Breakthrough RNA editing technique shows 95% efficacy in treating ALS",
    content: "Our revolutionary CRISPR-Cas13 system has achieved unprecedented success in treating neurological disorders by precisely editing RNA molecules without altering DNA. In clinical trials with ALS patients, we observed 95% improvement in motor function and significant slowing of disease progression. This approach opens new therapeutic avenues for previously untreatable conditions.",
    metrics: {
      "Clinical Efficacy": "95%",
      "Safety Score": "98.7%",
      "Patient Cohort": "847 participants",
      "Treatment Duration": "18 months"
    },
    tags: ["CRISPR", "RNA Editing", "ALS", "Neurological", "Clinical Trial"],
    priority: "hero",
    verified: true,
    publishDate: "2024-01-15",
    readTime: "8 min read",
    impact: "high"
  },
  {
    id: 2,
    title: "AI-Driven Protein Folding Predictions Revolutionize Drug Discovery",
    category: "Computational Biology",
    icon: faBrain,
    preview: "AlphaFold successor achieves 99.8% accuracy in predicting membrane protein structures",
    content: "Our next-generation AI model surpasses AlphaFold with 99.8% accuracy in predicting complex membrane protein structures. This breakthrough has accelerated drug discovery timelines by 300% and identified novel therapeutic targets for cancer, Alzheimer's, and rare genetic diseases.",
    metrics: {
      "Prediction Accuracy": "99.8%",
      "Structures Solved": "2.3M",
      "Drug Targets Identified": "1,247",
      "Time Reduction": "300%"
    },
    tags: ["AI", "Protein Folding", "Drug Discovery", "AlphaFold", "Membrane Proteins"],
    priority: "featured",
    verified: true,
    publishDate: "2024-01-12",
    readTime: "6 min read",
    impact: "high"
  },
  {
    id: 3,
    title: "Quantum-Enhanced Metabolomics Reveals Hidden Disease Pathways",
    category: "Quantum Biology",
    icon: faAtom,
    preview: "Quantum sensing detects femtomolar metabolite concentrations in live cells",
    content: "Using quantum-enhanced sensing technologies, we achieved femtomolar sensitivity in detecting metabolites within living cells. This unprecedented precision revealed previously unknown metabolic pathways involved in cancer metabolism, autoimmune diseases, and aging processes.",
    metrics: {
      "Detection Limit": "10^-15 M",
      "Metabolites Tracked": "12,847",
      "Pathways Discovered": "89",
      "Resolution Improvement": "1000x"
    },
    tags: ["Quantum Biology", "Metabolomics", "Single Cell", "Cancer", "Aging"],
    priority: "featured",
    verified: true,
    publishDate: "2024-01-10",
    readTime: "7 min read",
    impact: "high"
  },
  {
    id: 4,
    title: "Engineered Organoids Model Human Disease with 98% Fidelity",
    category: "Tissue Engineering",
    icon: faMicroscope,
    preview: "Brain organoids replicate Alzheimer's pathology with unprecedented accuracy",
    content: "Our engineered brain organoids achieve 98% fidelity in modeling human Alzheimer's disease pathology, including amyloid plaques, tau tangles, and neuroinflammation. These models accelerate drug testing and reveal disease mechanisms impossible to study in traditional models.",
    metrics: {
      "Model Fidelity": "98%",
      "Drug Candidates Tested": "2,341",
      "Success Rate": "34%",
      "Time Savings": "5 years"
    },
    tags: ["Organoids", "Alzheimer's", "Disease Modeling", "Drug Testing", "Brain"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-08",
    readTime: "5 min read",
    impact: "medium"
  },
  {
    id: 5,
    title: "Single-Cell Spatial Omics Maps Tumor Microenvironments",
    category: "Spatial Biology",
    icon: faMagnifyingGlass,
    preview: "Revolutionary imaging reveals cellular interactions driving cancer resistance",
    content: "Our spatial omics platform maps single-cell interactions within tumor microenvironments at nanometer resolution. We discovered novel cellular communication networks that drive therapy resistance and identified targets for combination treatments.",
    metrics: {
      "Spatial Resolution": "50 nm",
      "Cells Profiled": "10M+",
      "Interaction Networks": "847",
      "Resistance Mechanisms": "23"
    },
    tags: ["Spatial Omics", "Tumor Microenvironment", "Cancer Resistance", "Single Cell"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-05",
    readTime: "6 min read",
    impact: "medium"
  },
  {
    id: 6,
    title: "Microbiome Engineering Reverses Type 2 Diabetes",
    category: "Microbiome Therapeutics",
    icon: faHeart,
    preview: "Engineered gut bacteria produce insulin in diabetic patients",
    content: "Our engineered gut bacteria successfully produce human insulin in response to glucose levels, effectively reversing Type 2 diabetes in 89% of clinical trial participants. This living medicine approach offers a permanent solution to diabetes management.",
    metrics: {
      "Remission Rate": "89%",
      "HbA1c Reduction": "3.2%",
      "Patient Satisfaction": "97%",
      "Side Effects": "Minimal"
    },
    tags: ["Microbiome", "Diabetes", "Synthetic Biology", "Living Medicine", "Insulin"],
    priority: "standard",
    verified: false,
    publishDate: "2024-01-03",
    readTime: "7 min read",
    impact: "high"
  },
  {
    id: 7,
    title: "Liquid Biopsy Detects Cancer 5 Years Before Symptoms",
    category: "Diagnostic Innovation",
    icon: faFlask,
    preview: "Circulating tumor DNA analysis enables ultra-early cancer detection",
    content: "Our liquid biopsy technology detects circulating tumor DNA signatures up to 5 years before clinical symptoms appear. With 96% sensitivity and 99% specificity, this blood test could revolutionize cancer screening and early intervention.",
    metrics: {
      "Early Detection": "5 years",
      "Sensitivity": "96%",
      "Specificity": "99%",
      "Cancer Types": "20+"
    },
    tags: ["Liquid Biopsy", "Early Detection", "Cancer Screening", "ctDNA", "Precision Medicine"],
    priority: "featured",
    verified: false,
    publishDate: "2024-01-01",
    readTime: "5 min read",
    impact: "high"
  },
  {
    id: 8,
    title: "Retinal Regeneration Using iPSC-Derived Organoids",
    category: "Regenerative Medicine",
    icon: faEye,
    preview: "Blind patients regain sight through stem cell-derived retinal implants",
    content: "Our iPSC-derived retinal organoids successfully restored vision in 78% of patients with inherited blindness. These bioengineered retinas integrate seamlessly with existing neural networks and show long-term stability.",
    metrics: {
      "Vision Restoration": "78%",
      "Visual Acuity": "20/40",
      "Integration Success": "94%",
      "Follow-up": "3 years"
    },
    tags: ["iPSC", "Retinal Regeneration", "Stem Cells", "Blindness", "Organoids"],
    priority: "standard",
    verified: true,
    publishDate: "2023-12-28",
    readTime: "6 min read",
    impact: "medium"
  },
  {
    id: 9,
    title: "Lung-on-Chip Models Predict COVID Variant Severity",
    category: "Organ-on-Chip",
    icon: faLungs,
    preview: "Microfluidic lung models forecast pandemic responses weeks ahead",
    content: "Our lung-on-chip models accurately predict COVID-19 variant severity and transmission patterns weeks before clinical data emerges. This technology enables proactive public health responses and accelerated vaccine development.",
    metrics: {
      "Prediction Accuracy": "92%",
      "Time Advantage": "3-4 weeks",
      "Variants Tested": "47",
      "Validation Studies": "12"
    },
    tags: ["Organ-on-Chip", "COVID-19", "Pandemic Prediction", "Microfluidics", "Public Health"],
    priority: "standard",
    verified: true,
    publishDate: "2023-12-25",
    readTime: "4 min read",
    impact: "medium"
  },
  {
    id: 10,
    title: "Bioprinted Kidneys Pass Transplant Safety Trials",
    category: "Bioprinting",
    icon: faDroplet,
    preview: "3D-printed kidneys function normally in preclinical transplant studies",
    content: "Our 3D-bioprinted kidneys demonstrate normal function in preclinical transplant studies, filtering blood and producing urine for over 6 months. Human trials are set to begin next year, potentially ending the organ shortage crisis.",
    metrics: {
      "Function Duration": "6+ months",
      "Filtration Rate": "Normal",
      "Rejection Rate": "0%",
      "Human Trial Start": "2024"
    },
    tags: ["Bioprinting", "Kidney Transplant", "Organ Shortage", "3D Printing", "Regenerative Medicine"],
    priority: "standard",
    verified: false,
    publishDate: "2023-12-22",
    readTime: "8 min read",
    impact: "high"
  },
  {
    id: 11,
    title: "Nanobots Clear Arterial Plaques in Cardiovascular Trials",
    category: "Diagnostic Innovation",
    icon: faHeart,
    preview: "Targeted nanobots remove 87% of arterial blockages without surgery",
    content: "Microscopic nanobots designed to target atherosclerotic plaques successfully cleared 87% of arterial blockages in phase II trials. These programmable devices offer a non-invasive alternative to bypass surgery.",
    metrics: {
      "Plaque Reduction": "87%",
      "Success Rate": "94%",
      "Treatment Time": "4 hours",
      "Side Effects": "None"
    },
    tags: ["Nanobots", "Cardiovascular", "Atherosclerosis", "Non-invasive", "Targeted Therapy"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-20",
    readTime: "5 min read",
    impact: "medium"
  },
  {
    id: 12,
    title: "CRISPR 3.0 Achieves Multiplexed Gene Editing",
    category: "Gene Therapy",
    icon: faDna,
    preview: "Next-gen CRISPR edits 50+ genes simultaneously with 99.2% precision",
    content: "CRISPR 3.0 enables simultaneous editing of over 50 genes with unprecedented precision. This multiplexed approach accelerates genetic disease research and opens new therapeutic possibilities.",
    metrics: {
      "Genes Edited": "50+",
      "Precision Rate": "99.2%",
      "Efficiency": "300% faster",
      "Applications": "12"
    },
    tags: ["CRISPR", "Multiplexed Editing", "Gene Therapy", "Precision Medicine", "Genetic Disease"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-18",
    readTime: "6 min read",
    impact: "medium"
  },
  {
    id: 13,
    title: "Brain Organoids Reveal Autism Spectrum Disorder Mechanisms",
    category: "Tissue Engineering",
    icon: faBrain,
    preview: "Lab-grown brain tissue models uncover cellular pathways in autism",
    content: "Patient-derived brain organoids reveal disrupted neural connectivity patterns in autism spectrum disorders. These models identify potential therapeutic targets for early intervention.",
    metrics: {
      "Patient Samples": "156",
      "Pathway Targets": "23",
      "Model Accuracy": "91%",
      "Therapeutic Leads": "8"
    },
    tags: ["Brain Organoids", "Autism", "Neural Development", "Disease Modeling", "Therapeutic Targets"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-16",
    readTime: "7 min read",
    impact: "medium"
  },
  {
    id: 14,
    title: "Epigenetic Memory Transfer Between Cells Discovered",
    category: "Computational Biology",
    icon: faAtom,
    preview: "Cells pass epigenetic information to neighbors via extracellular vesicles",
    content: "Research reveals that cells transfer epigenetic memories through extracellular vesicles, influencing neighboring cell behavior. This discovery reshapes our understanding of cellular communication.",
    metrics: {
      "Transfer Efficiency": "78%",
      "Cell Types": "12",
      "Memory Duration": "72 hours",
      "Signal Range": "500 μm"
    },
    tags: ["Epigenetics", "Cell Communication", "Extracellular Vesicles", "Memory Transfer", "Cellular Biology"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-14",
    readTime: "8 min read",
    impact: "medium"
  },
  {
    id: 15,
    title: "Microfluidic Liver Chips Predict Drug Toxicity",
    category: "Organ-on-Chip",
    icon: faFlask,
    preview: "Liver-on-chip models show 95% accuracy in predicting hepatotoxicity",
    content: "Advanced microfluidic liver chips demonstrate 95% accuracy in predicting drug-induced liver toxicity, potentially reducing pharmaceutical development costs by billions.",
    metrics: {
      "Prediction Accuracy": "95%",
      "Drugs Tested": "1,847",
      "Cost Reduction": "$2.3B",
      "Time Savings": "18 months"
    },
    tags: ["Liver-on-Chip", "Drug Toxicity", "Hepatotoxicity", "Pharmaceutical", "Microfluidics"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-13",
    readTime: "5 min read",
    impact: "medium"
  },
  {
    id: 16,
    title: "Synthetic Blood Vessels Integrate with Human Circulatory System",
    category: "Bioprinting",
    icon: faHeart,
    preview: "3D-printed blood vessels successfully integrate in human patients",
    content: "Bioprinted blood vessels show complete integration with the human circulatory system after 6 months. These synthetic vessels could eliminate the need for vessel grafts from other body parts.",
    metrics: {
      "Integration Rate": "96%",
      "Patient Success": "89%",
      "Follow-up": "6 months",
      "Complications": "2%"
    },
    tags: ["Bioprinting", "Blood Vessels", "Vascular Surgery", "Tissue Engineering", "Regenerative Medicine"],
    priority: "standard",
    verified: false,
    publishDate: "2024-01-11",
    readTime: "6 min read",
    impact: "medium"
  },
  {
    id: 17,
    title: "Engineered Bacteria Produce Rare Earth Elements",
    category: "Microbiome Therapeutics",
    icon: faAtom,
    preview: "Modified gut bacteria extract valuable metals from industrial waste",
    content: "Genetically engineered bacteria can extract rare earth elements from electronic waste with 78% efficiency. This bioextraction method offers a sustainable alternative to traditional mining.",
    metrics: {
      "Extraction Efficiency": "78%",
      "Metal Types": "15",
      "Waste Processed": "2.3 tons",
      "Purity Level": "99.1%"
    },
    tags: ["Synthetic Biology", "Rare Earth Elements", "Bioextraction", "Sustainability", "Electronic Waste"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-09",
    readTime: "4 min read",
    impact: "medium"
  },
  {
    id: 18,
    title: "Quantum Sensors Detect Single Protein Conformations",
    category: "Quantum Biology",
    icon: faMagnifyingGlass,
    preview: "Quantum magnetometry reveals protein folding dynamics in real-time",
    content: "Quantum sensors achieve single-protein sensitivity, revealing real-time folding dynamics. This breakthrough enables direct observation of protein misfolding diseases like Alzheimer's.",
    metrics: {
      "Sensitivity": "Single protein",
      "Time Resolution": "10 ms",
      "Protein Types": "47",
      "Detection Range": "1-100 kDa"
    },
    tags: ["Quantum Sensors", "Protein Folding", "Single Molecule", "Alzheimer's", "Real-time Imaging"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-07",
    readTime: "7 min read",
    impact: "medium"
  },
  {
    id: 19,
    title: "Immune Cell Reprogramming Reverses Autoimmune Diseases",
    category: "Regenerative Medicine",
    icon: faHeart,
    preview: "T-cell reprogramming shows 82% remission rate in multiple sclerosis",
    content: "Reprogrammed T-cells successfully induce remission in 82% of multiple sclerosis patients. This cell therapy approach could transform treatment of autoimmune disorders.",
    metrics: {
      "Remission Rate": "82%",
      "Patient Cohort": "94",
      "Relapse Rate": "8%",
      "Treatment Duration": "3 months"
    },
    tags: ["T-cell Therapy", "Multiple Sclerosis", "Autoimmune", "Cell Reprogramming", "Immunotherapy"],
    priority: "standard",
    verified: false,
    publishDate: "2024-01-06",
    readTime: "6 min read",
    impact: "medium"
  },
  {
    id: 20,
    title: "Artificial Synapses Restore Memory in Brain-Injured Patients",
    category: "Tissue Engineering",
    icon: faBrain,
    preview: "Bioelectronic implants restore lost memories with 74% success rate",
    content: "Artificial synapses successfully restore memory formation in patients with traumatic brain injury. These bioelectronic devices bridge damaged neural pathways.",
    metrics: {
      "Memory Restoration": "74%",
      "Patients Treated": "31",
      "Neural Pathways": "156",
      "Improvement Time": "2 weeks"
    },
    tags: ["Artificial Synapses", "Memory Restoration", "Brain Injury", "Bioelectronics", "Neural Implants"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-04",
    readTime: "8 min read",
    impact: "medium"
  },
  {
    id: 21,
    title: "Lab-Grown Corneas Restore Sight to Blind Patients",
    category: "Regenerative Medicine",
    icon: faEye,
    preview: "Bioengineered corneas achieve 88% success rate in clinical trials",
    content: "Laboratory-grown corneas restore vision in 88% of patients with corneal blindness. These bioengineered tissues eliminate the need for donor corneas.",
    metrics: {
      "Success Rate": "88%",
      "Patients Treated": "67",
      "Vision Improvement": "20/40",
      "Rejection Rate": "3%"
    },
    tags: ["Corneal Transplant", "Bioengineering", "Vision Restoration", "Tissue Engineering", "Blindness"],
    priority: "standard",
    verified: true,
    publishDate: "2024-01-02",
    readTime: "5 min read",
    impact: "medium"
  }
];

export const categories = [
  { id: 'all', name: 'All Discoveries', icon: faFlask, color: '#ffffff' },
  { id: 'Gene Therapy', name: 'Gene Therapy', icon: faDna, color: '#ff6b6b' },
  { id: 'Computational Biology', name: 'AI & Computing', icon: faBrain, color: '#4ecdc4' },
  { id: 'Quantum Biology', name: 'Quantum Biology', icon: faAtom, color: '#45b7d1' },
  { id: 'Tissue Engineering', name: 'Tissue Engineering', icon: faMicroscope, color: '#feca57' },
  { id: 'Spatial Biology', name: 'Spatial Biology', icon: faMagnifyingGlass, color: '#ff9ff3' },
  { id: 'Microbiome Therapeutics', name: 'Microbiome', icon: faHeart, color: '#54a0ff' },
  { id: 'Diagnostic Innovation', name: 'Diagnostics', icon: faFlask, color: '#5f27cd' },
  { id: 'Regenerative Medicine', name: 'Regeneration', icon: faEye, color: '#00d2d3' },
  { id: 'Organ-on-Chip', name: 'Organ Models', icon: faLungs, color: '#ff6348' },
  { id: 'Bioprinting', name: 'Bioprinting', icon: faDroplet, color: '#2ed573' }
];

export const impactLevels = {
  high: { name: 'High Impact', color: '#ff6b6b', intensity: 3 },
  medium: { name: 'Medium Impact', color: '#feca57', intensity: 2 },
  low: { name: 'Standard Impact', color: '#48dbfb', intensity: 1 }
};